import { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';

const BulkInvoiceModal = ({ isOpen, onClose, feeRecords = [] }) => {
  const [selectedRecords, setSelectedRecords] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);

  if (!isOpen) return null;

  // Filter records that are eligible for invoicing (pending or draft status)
  const eligibleRecords = feeRecords?.filter(
    (record) => record?.status === 'pending' || record?.status === 'draft'
  );

  const handleSelectAll = () => {
    if (selectedRecords?.length === eligibleRecords?.length) {
      setSelectedRecords([]);
    } else {
      setSelectedRecords(eligibleRecords?.map((record) => record?.id));
    }
  };

  const handleToggleRecord = (recordId) => {
    setSelectedRecords((prev) =>
      prev?.includes(recordId)
        ? prev?.filter((id) => id !== recordId)
        : [...prev, recordId]
    );
  };

  const handleGenerateInvoices = () => {
    if (selectedRecords?.length === 0) {
      alert('⚠️ Please select at least one record to generate invoices.');
      return;
    }

    setIsProcessing(true);
    
    // Simulate invoice generation process
    setTimeout(() => {
      setIsProcessing(false);
      alert(`✅ Successfully generated ${selectedRecords?.length} invoice(s)!`);
      setSelectedRecords([]);
      onClose();
    }, 2000);
  };

  const calculateTotals = () => {
    const selected = eligibleRecords?.filter((record) =>
      selectedRecords?.includes(record?.id)
    );
    
    return {
      count: selected?.length,
      totalGrossFee: selected?.reduce((sum, record) => sum + record?.grossFee, 0),
      totalNetBalance: selected?.reduce((sum, record) => sum + record?.netBalance, 0),
    };
  };

  const totals = calculateTotals();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
              <Icon name="Send" size={20} color="#3B82F6" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-slate-900">
                Generate Bulk Invoices
              </h2>
              <p className="text-sm text-slate-600">
                Select fee records to generate invoices
              </p>
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <Icon name="X" size={20} />
          </Button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
          {eligibleRecords?.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
                <Icon name="FileText" size={32} color="#64748B" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">
                No Eligible Records
              </h3>
              <p className="text-slate-600">
                There are no pending or draft fee records available for invoice generation.
              </p>
            </div>
          ) : (
            <>
              {/* Select All */}
              <div className="flex items-center justify-between mb-4 pb-4 border-b border-slate-200">
                <div className="flex items-center gap-3">
                  <Checkbox
                    checked={selectedRecords?.length === eligibleRecords?.length}
                    onCheckedChange={handleSelectAll}
                  />
                  <span className="text-sm font-medium text-slate-900">
                    Select All ({eligibleRecords?.length} records)
                  </span>
                </div>
                <div className="text-sm text-slate-600">
                  {selectedRecords?.length} selected
                </div>
              </div>

              {/* Records List */}
              <div className="space-y-3">
                {eligibleRecords?.map((record) => (
                  <div
                    key={record?.id}
                    className={`p-4 rounded-lg border transition-all ${
                      selectedRecords?.includes(record?.id)
                        ? 'border-blue-500 bg-blue-50' :'border-slate-200 bg-white hover:border-slate-300'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <Checkbox
                        checked={selectedRecords?.includes(record?.id)}
                        onCheckedChange={() => handleToggleRecord(record?.id)}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-2">
                          <div>
                            <h4 className="text-sm font-semibold text-slate-900">
                              {record?.month}
                            </h4>
                            <p className="text-xs text-slate-600">
                              {record?.invoiceNumber}
                            </p>
                          </div>
                          <span
                            className={`px-2 py-1 rounded text-xs font-medium ${
                              record?.status === 'pending' ?'bg-yellow-100 text-yellow-700' :'bg-gray-100 text-gray-700'
                            }`}
                          >
                            {record?.status}
                          </span>
                        </div>
                        <div className="grid grid-cols-3 gap-4 text-xs">
                          <div>
                            <span className="text-slate-600">Gross Fee:</span>
                            <p className="font-semibold text-slate-900">
                              ${record?.grossFee?.toLocaleString()}
                            </p>
                          </div>
                          <div>
                            <span className="text-slate-600">Tonnage:</span>
                            <p className="font-semibold text-slate-900">
                              {record?.totalTonnage?.toLocaleString()} MT
                            </p>
                          </div>
                          <div>
                            <span className="text-slate-600">Net Balance:</span>
                            <p className="font-semibold text-slate-900">
                              ${record?.netBalance?.toLocaleString()}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Summary */}
              {selectedRecords?.length > 0 && (
                <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <h3 className="text-sm font-semibold text-slate-900 mb-3">
                    Invoice Summary
                  </h3>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <span className="text-xs text-slate-600">
                        Selected Records
                      </span>
                      <p className="text-lg font-bold text-slate-900">
                        {totals?.count}
                      </p>
                    </div>
                    <div>
                      <span className="text-xs text-slate-600">
                        Total Gross Fee
                      </span>
                      <p className="text-lg font-bold text-slate-900">
                        ${totals?.totalGrossFee?.toLocaleString()}
                      </p>
                    </div>
                    <div>
                      <span className="text-xs text-slate-600">
                        Total Net Balance
                      </span>
                      <p className="text-lg font-bold text-green-600">
                        ${totals?.totalNetBalance?.toLocaleString()}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        {eligibleRecords?.length > 0 && (
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-200 bg-slate-50">
            <Button variant="outline" onClick={onClose} disabled={isProcessing}>
              Cancel
            </Button>
            <Button
              variant="default"
              onClick={handleGenerateInvoices}
              disabled={selectedRecords?.length === 0 || isProcessing}
            >
              {isProcessing ? (
                <>
                  <Icon name="Loader" size={16} className="animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <Icon name="Send" size={16} />
                  Generate {selectedRecords?.length > 0 ? `(${selectedRecords?.length})` : ''} Invoice{selectedRecords?.length !== 1 ? 's' : ''}
                </>
              )}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default BulkInvoiceModal;